/**
 * PIV-48: data hooks for the background activity in the roster and the to-do window in the bot view,
 * read from bots.activity and bots.todo (contracts/src/bot-activity.ts). Both need the ticket board;
 * without it the roster stays as before and the window is hidden.
 */
import type { BoardEvent, BotRequestItem, BotSessionItem } from "@rakazo/contracts";
import { IDLE_TIMEOUT_MS, isRetryableError } from "@rakazo/core";
import type { QueryClient } from "@tanstack/react-query";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "./bootstrap";
import type { BotTodoItem } from "./bot-activity";
import { activityByBot } from "./bot-activity";
import type { SpaceQueries } from "./query";
import { useSpaceQueries } from "./query";
import { isReviewFrame, reviewKeys } from "./review-inbox-query";
import type { RpcClient } from "./rpc";
import { rpc, rpcForSpace, selectedSpaceId } from "./rpc";
import { useBoundSpaceId, useSpaceRpc } from "./space-rpc";
import { createStreamBackoff } from "./stream-backoff";

/**
 * Fallback polling, only in a visible tab and only while the board stream is down or has been
 * silent for IDLE_TIMEOUT_MS (RAK-71, as APP-2 G1). While the stream is open, nothing polls.
 */
const REFRESH_MS = 15_000;

/**
 * Slow polling of bots.activity while the stream is live (RAK-71 M1, transition): the board
 * stream today carries only ticket changes, so runs without one (routines) reach the roster only
 * by polling. Goes away once runs arrive as board events with kind "run" (RAK-74).
 */
const LIVE_ACTIVITY_REFRESH_MS = 60_000;

const EMPTY_TODO: readonly BotTodoItem[] = [];

function useTicketBoardEnabled(): boolean {
  return useSyncExternalStore(subscribeTicketBoardEnabled, getTicketBoardEnabled, () => false);
}

/**
 * Marks activity and every to-do list of the space stale, so open ones load again. The keys
 * carry user and space, so this never touches another account's cache.
 */
export function invalidateBotActivity(client: QueryClient, queries: SpaceQueries): Promise<void> {
  return Promise.all([
    // A burst of events must not cancel and restart the read already on its way (G2).
    client.invalidateQueries({ queryKey: queries.bots.activity.key() }, { cancelRefetch: false }),
    client.invalidateQueries({ queryKey: queries.bots.todo.key() }, { cancelRefetch: false }),
  ]).then(() => undefined);
}

/**
 * Open, non-silent board streams per space; the to-do window reads this too. A count per key,
 * so one stream ending does not mark the space dead while another is still open (G3).
 */
const liveCounts = new Map<string, number>();
const liveListeners = new Set<() => void>();

function changeSpaceLive(key: string, delta: 1 | -1): void {
  const next = (liveCounts.get(key) ?? 0) + delta;
  if (next > 0) liveCounts.set(key, next);
  else liveCounts.delete(key);
  for (const listener of liveListeners) listener();
}

function isSpaceLive(key: string): boolean {
  return (liveCounts.get(key) ?? 0) > 0;
}

function subscribeLive(listener: () => void): () => void {
  liveListeners.add(listener);
  return () => {
    liveListeners.delete(listener);
  };
}

/** Key of the space useSpaceQueries acts on (a bound settings space or the selected one). */
function useSpaceKey(): string {
  return useBoundSpaceId() ?? selectedSpaceId() ?? "";
}

/**
 * Whether the board stream of this space is open and not silent; then to-do lists do not poll
 * and activity polls only every 60 s (M1).
 */
function useBoardLive(key: string): boolean {
  const read = useCallback(() => isSpaceLive(key), [key]);
  return useSyncExternalStore(subscribeLive, read, () => false);
}

/**
 * Client of the space useSpaceQueries acts on, so the stream and the reads it invalidates always
 * mean the same space (G3).
 */
function useSpaceStreamClient(key: string): RpcClient {
  return key ? rpcForSpace(key) : rpc;
}

/**
 * A frame without board or ticket loads nothing: the server's heartbeat and, until RAK-71 reads
 * kind, run frames (boards.subscribe). A ticket change carries one of the two.
 */
function isHeartbeat(event: BoardEvent): boolean {
  return !("boardId" in event && event.boardId) && !("ticketId" in event && event.ticketId);
}

/**
 * Board events (ticket sessions start and end, any ticket change) refresh activity and to-do
 * lists at once; a heartbeat only proves the stream lives and loads nothing. The stream counts
 * as live from its opening until it ends or nothing arrived for IDLE_TIMEOUT_MS. A dropped
 * stream reconnects when the error is retryable, after a growing wait (stream-backoff), and
 * loads once after reconnecting; until then, and after a refusal, the 15 s polling runs. The
 * stream carries ticket changes only, not runs without a ticket (routines; M1, RAK-74). A review
 * frame (RAK-425) also reloads the review inbox, its list and badge.
 */
function useBotActivityLive(
  enabled: boolean,
  queries: SpaceQueries,
  api: RpcClient,
  key: string,
): void {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let currentController: AbortController | undefined;
    let cancelRetry: (() => void) | undefined;
    let silence: ReturnType<typeof setTimeout> | undefined;
    // This instance's share of the space's live count.
    let counted = false;
    const setLive = (live: boolean) => {
      if (counted === live) return;
      counted = live;
      changeSpaceLive(key, live ? 1 : -1);
    };
    const backoff = createStreamBackoff(REFRESH_MS);
    const heard = () => {
      clearTimeout(silence);
      if (!active) return;
      setLive(true);
      silence = setTimeout(() => setLive(false), IDLE_TIMEOUT_MS);
    };
    const lost = () => {
      clearTimeout(silence);
      setLive(false);
    };
    void (async () => {
      while (active) {
        const controller = new AbortController();
        currentController = controller;
        let failure: unknown;
        try {
          const stream = await api.boards.subscribe(undefined, { signal: controller.signal });
          if (!active) return;
          heard();
          // Whatever changed while the stream was down.
          void invalidateBotActivity(client, queries);
          for await (const event of stream) {
            if (!active) return;
            backoff.reset();
            heard();
            if (!isHeartbeat(event)) void invalidateBotActivity(client, queries);
            if (isReviewFrame(event)) void client.invalidateQueries({ queryKey: reviewKeys.all });
          }
        } catch (cause) {
          failure = cause;
        } finally {
          controller.abort();
          if (active) lost();
        }
        if (!active || !isRetryableError(failure)) return;
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, backoff.next(failure));
          cancelRetry = () => {
            clearTimeout(timer);
            resolve();
          };
        });
        cancelRetry = undefined;
      }
    })();
    return () => {
      active = false;
      currentController?.abort();
      cancelRetry?.();
      clearTimeout(silence);
      setLive(false);
    };
  }, [enabled, client, queries, api, key]);
}

/**
 * Background activity of all bots as a map by bot id, kept live by board events; polls every
 * 15 s while the stream is down or silent, and every 60 s while it is live (M1, until RAK-74).
 */
export function useBotActivityMap(enabled = true) {
  const queries = useSpaceQueries();
  const board = useTicketBoardEnabled();
  const key = useSpaceKey();
  const api = useSpaceStreamClient(key);
  const on = enabled && board;
  useBotActivityLive(on, queries, api, key);
  const live = useBoardLive(key);
  const query = useQuery({
    ...queries.bots.activity.queryOptions(),
    enabled: on,
    refetchInterval: live ? LIVE_ACTIVITY_REFRESH_MS : REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  // Same map while the data is unchanged, so the roster prop stays stable across Shell renders.
  return useMemo(() => activityByBot(on ? query.data : undefined), [on, query.data]);
}

/**
 * To-do rows of one bot in server order (BotTodoWindow orders them); empty while loading, without
 * a bot or board. Polls only while the board stream is down or silent. RAK-91 G2: `team` is the
 * contract's flag (the bot oversees others), null until the first answer.
 */
export function useBotTodo(botId: string | null | undefined) {
  const queries = useSpaceQueries();
  const board = useTicketBoardEnabled();
  const live = useBoardLive(useSpaceKey());
  const query = useQuery({
    ...queries.bots.todo.queryOptions({ input: botId && board ? { botId } : skipToken }),
    refetchInterval: live ? false : REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  // bots.todo answers { items, team } (G1, RAK-92).
  const data = board && botId ? query.data : undefined;
  const items = data?.items ?? EMPTY_TODO;
  const team = data?.team ?? null;
  return useMemo(() => ({ items, team }), [items, team]);
}

const NO_PENDING: ReadonlySet<string> = new Set();

/**
 * RAK-91: pause, resume and remove for the to-do rail (tickets.pause/resume/remove, RAK-92). The
 * server checks space and owner; the client only sends the ticket id. A row's id is pending while
 * its call runs (a second click is ignored); afterwards the to-do list and roster reload, also
 * when the board stream already brought the change. Errors go to onError.
 */
export function useTodoRowActions(onError: (error: unknown) => void) {
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const client = useQueryClient();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(NO_PENDING);
  const running = useRef(new Set<string>());
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });
  const run = useCallback(
    async (id: string, call: () => Promise<unknown>) => {
      if (running.current.has(id)) return;
      running.current.add(id);
      setPendingIds(new Set(running.current));
      try {
        await call();
      } catch (error) {
        onErrorRef.current(error);
      } finally {
        running.current.delete(id);
        setPendingIds(running.current.size ? new Set(running.current) : NO_PENDING);
        void client.invalidateQueries({ queryKey: queries.bots.todo.key() });
        void client.invalidateQueries({ queryKey: queries.bots.activity.key() });
      }
    },
    [client, queries],
  );
  return useMemo(
    () => ({
      onPause: (id: string) => void run(id, () => rpc.tickets.pause({ id })),
      onResume: (id: string) => void run(id, () => rpc.tickets.resume({ id })),
      onRemove: (id: string) => void run(id, () => rpc.tickets.remove({ id })),
      pendingIds,
    }),
    [rpc, run, pendingIds],
  );
}

const EMPTY_REQUESTS: BotRequestItem[] = [];

/**
 * jhl-Bots (Arbeitsmodell): the person's requests to the bot that still need someone, from the
 * same bots.todo answer as useBotTodo (one query).
 */
export function useBotTodoRequests(botId: string | null | undefined): readonly BotRequestItem[] {
  const queries = useSpaceQueries();
  const board = useTicketBoardEnabled();
  const live = useBoardLive(useSpaceKey());
  const query = useQuery({
    ...queries.bots.todo.queryOptions({ input: botId && board ? { botId } : skipToken }),
    refetchInterval: live ? false : REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  return (board && botId ? query.data?.requests : undefined) ?? EMPTY_REQUESTS;
}

const EMPTY_SESSIONS: BotSessionItem[] = [];
/** Sessions run without board events of their own, so their list refreshes on a short poll. */
const SESSIONS_REFRESH_MS = 10_000;

/**
 * jhl-Bots (Sitzungen): a bot's work sessions, open ones first; with `includeClosed` also those
 * ended within the week. Empty without the board.
 */
export function useBotSessions(
  botId: string | null | undefined,
  includeClosed = false,
): readonly BotSessionItem[] {
  const queries = useSpaceQueries();
  const board = useTicketBoardEnabled();
  const query = useQuery({
    ...queries.bots.sessions.queryOptions({
      input: botId && board ? { botId, includeClosed } : skipToken,
    }),
    refetchInterval: SESSIONS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  return (board && botId ? query.data : undefined) ?? EMPTY_SESSIONS;
}
